import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "@/components/Primitives";
import { fmtDateTime } from "@/lib/format";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/StatusChip";
import { Plus } from "lucide-react";

const ROLES = [
  { value: "tenant_owner", label: "Tenant Owner" }, { value: "sales_manager", label: "Sales Manager" },
  { value: "sales_rep", label: "Sales Representative" }, { value: "readonly", label: "Read-Only / Support" },
];

const COMMISSION_PLANS = [
  { value: "current_v3_7", label: "Current V3.7", hint: "6-month: 30% monthly. 12-month: 35% month 1, then 30% monthly." },
  { value: "legacy", label: "Legacy / Existing Agreement", hint: "Preserves the contractor's historical signed commission arrangement." },
];

function planLabel(value) {
  return value === "current_v3_7" ? "Current V3.7" : "Legacy";
}

export default function Users() {
  const { can } = useAuth();
  const isAdmin = can("tenant_owner", "platform_owner");
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ role: "sales_rep", commission_plan: "current_v3_7" });
  const [editUser, setEditUser] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [avail, setAvail] = useState(null);
  const [planUser, setPlanUser] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setRows(r.data.items)), []);
  useEffect(() => { load(); }, [load]);

  const saveBooking = async () => {
    try { await api.put(`/users/${editUser.id}`, { booking_url: editUrl }); toast.success("Scheduling Link saved"); setEditUser(null); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const saveAvail = async () => {
    try {
      await api.put(`/users/${avail.id}`, {
        unavailable_for_new_leads: !!avail.unavailable_for_new_leads,
        backup_user_id: avail.backup_user_id || null,
      });
      toast.success("Availability updated"); setAvail(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const savePlan = async () => {
    try {
      await api.put(`/users/${planUser.id}`, { commission_plan: planUser.commission_plan || "legacy" });
      toast.success("Commission plan updated"); setPlanUser(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success(`Invitation sent to ${form.email}`);
      setDialog(false); setForm({ role: "sales_rep", commission_plan: "current_v3_7" }); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const resendInvite = async (u) => {
    try { await api.post(`/users/${u.id}/resend-invite`); toast.success("Invitation resent"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const cancelInvite = async (u) => {
    try { await api.post(`/users/${u.id}/cancel-invite`); toast.success("Invitation cancelled"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const securityReset = async (u) => {
    if (!window.confirm(`Reset security for ${u.name}? This turns off their 2FA, clears trusted devices, and signs them out everywhere. They'll reconfigure at next login. You will not see their passwords or codes.`)) return;
    try { await api.post("/auth/admin/security-reset", { user_id: u.id }); toast.success(`Security reset for ${u.name}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openCrm = (u) => {
    if (!(u.role === "sales_rep" || u.role === "sales_manager")) return;
    localStorage.setItem("cc_crm_scope", u.id);
    localStorage.setItem("cc_crm_scope_name", u.name || "Salesperson");
    window.location.assign("/");
  };

  return (
    <div data-testid="page-users">
      <PageHeader title="Users" subtitle="Manage staff accounts, roles and access.">
        {isAdmin && <Button size="sm" onClick={() => setDialog(true)} data-testid="create-user-btn"><Plus className="h-4 w-4" />Invite User</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th>
            <th className="px-4 py-3">Commission Plan</th><th className="px-4 py-3">Last Login</th><th className="px-4 py-3">Invite</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`user-${u.id}`}>
                <td className="px-4 py-3 font-semibold">
                  {(u.role === "sales_rep" || u.role === "sales_manager") ? (
                    <button onClick={() => openCrm(u)} className="text-left text-[#0094A8] hover:underline" data-testid={`open-crm-name-${u.id}`} title={`Open ${u.name}'s CRM`}>
                      {u.name}
                    </button>
                  ) : u.name}
                </td>
                <td className="px-4 py-3 text-slate-500">{u.email}</td>
                <td className="px-4 py-3">{ROLES.find((r) => r.value === u.role)?.label || u.role}</td>
                <td className="px-4 py-3">
                  {(u.role === "sales_rep" || u.role === "sales_manager") ? (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${u.commission_plan === "current_v3_7" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                      {planLabel(u.commission_plan)}
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-500">{u.last_login ? fmtDateTime(u.last_login) : "Never"}</td>
                <td className="px-4 py-3">
                  {u.invite_status && u.invite_status !== "Accepted" ? (
                    <div className="flex items-center gap-2">
                      <StatusChip value={u.invite_status} tone={u.invite_status === "Pending" ? "amber" : u.invite_status === "Cancelled" ? "slate" : "red"} />
                      {isAdmin && u.invite_status !== "Cancelled" && (
                        <>
                          <button onClick={() => resendInvite(u)} className="text-xs text-[#0094A8] hover:underline" data-testid={`resend-${u.id}`}>Resend</button>
                          <button onClick={() => cancelInvite(u)} className="text-xs text-[#C42607] hover:underline" data-testid={`cancel-${u.id}`}>Cancel</button>
                        </>
                      )}
                    </div>
                  ) : <StatusChip value="Accepted" tone="green" />}
                </td>
                <td className="px-4 py-3"><span className={`inline-flex items-center gap-1.5 text-xs font-medium ${u.active ? "text-[#5B880C]" : "text-slate-400"}`}><span className={`h-2 w-2 rounded-full ${u.active ? "bg-[#9ACC32]" : "bg-slate-300"}`} />{u.active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3 text-right">{isAdmin && (
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    {(u.role === "sales_rep" || u.role === "sales_manager") && (
                      <>
                        <button onClick={() => openCrm(u)} className="text-xs font-semibold text-[#0094A8] hover:underline" data-testid={`open-crm-${u.id}`}>Open CRM</button>
                        <button onClick={() => setPlanUser({ ...u, commission_plan: u.commission_plan || "legacy" })} className="text-xs text-[#0094A8] hover:underline" data-testid={`commission-plan-${u.id}`}>Commission Plan</button>
                        <button onClick={() => setAvail({ ...u })} className="text-xs text-[#0094A8] hover:underline" data-testid={`avail-${u.id}`}>
                          {u.unavailable_for_new_leads ? "Away" : "Availability"}
                        </button>
                      </>
                    )}
                    <button onClick={() => { setEditUser(u); setEditUrl(u.booking_url || ""); }} className="text-xs text-[#0094A8] hover:underline" data-testid={`booking-${u.id}`}>Scheduling Link</button>
                    <button onClick={() => securityReset(u)} className="text-xs text-[#0094A8] hover:underline" data-testid={`reset-2fa-${u.id}`}>Reset 2FA</button>
                    <Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`toggle-${u.id}`} />
                  </div>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display">Invite User</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Full Name</Label><Input className="mt-1" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" className="mt-1" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label className="text-xs">Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, commission_plan: (v === "sales_rep" || v === "sales_manager") ? (form.commission_plan || "current_v3_7") : null })}><SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            {(form.role === "sales_rep" || form.role === "sales_manager") && (
              <div><Label className="text-xs">Commission Plan</Label><Select value={form.commission_plan || "current_v3_7"} onValueChange={(v) => setForm({ ...form, commission_plan: v })}><SelectTrigger className="mt-1" data-testid="user-commission-plan"><SelectValue /></SelectTrigger><SelectContent>{COMMISSION_PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select><p className="mt-1 text-xs text-slate-400">New sales staff should normally use Current V3.7. Use Legacy only for an existing signed arrangement.</p></div>
            )}
            <div><Label className="text-xs">Temporary Password (optional)</Label><Input className="mt-1" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Defaults to secure invitation setup" /></div>
            <div><Label className="text-xs">Scheduling Link (optional)</Label><Input className="mt-1" value={form.booking_url || ""} onChange={(e) => setForm({ ...form, booking_url: e.target.value })} placeholder="https://outlook.office.com/bookwithme/..." data-testid="user-booking" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} data-testid="user-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!planUser} onOpenChange={(o) => !o && setPlanUser(null)}>
        <DialogContent data-testid="commission-plan-dialog">
          <DialogHeader><DialogTitle className="font-display">Commission Plan</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">Choose the signed commission arrangement for <span className="font-semibold text-slate-700">{planUser?.name}</span>. This affects new contracts only; historical commission snapshots stay unchanged.</p>
            <div className="space-y-2">
              {COMMISSION_PLANS.map((p) => (
                <label key={p.value} className={`block cursor-pointer rounded-xl border p-3 ${planUser?.commission_plan === p.value ? "border-[#00C9E0] bg-cyan-50/60" : "border-slate-200"}`}>
                  <div className="flex items-start gap-2">
                    <input type="radio" name="commission-plan" className="mt-1" checked={planUser?.commission_plan === p.value} onChange={() => setPlanUser({ ...planUser, commission_plan: p.value })} />
                    <div><div className="text-sm font-semibold text-slate-800">{p.label}</div><div className="text-xs text-slate-500">{p.hint}</div></div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPlanUser(null)}>Cancel</Button><Button onClick={savePlan} data-testid="commission-plan-save">Save Plan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="booking-dialog">
          <DialogHeader><DialogTitle className="font-display">Scheduling Link</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">Personal booking / scheduling URL for <span className="font-semibold text-slate-700">{editUser?.name}</span>. Used by the {"{{booking_link}}"} email merge field.</p>
            <div><Label className="text-xs">Scheduling Link (https://)</Label><Input className="mt-1" value={editUrl} onChange={(e) => setEditUrl(e.target.value)} placeholder="https://outlook.office.com/bookwithme/..." data-testid="booking-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button><Button onClick={saveBooking} data-testid="booking-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!avail} onOpenChange={(o) => !o && setAvail(null)}>
        <DialogContent data-testid="avail-dialog">
          <DialogHeader><DialogTitle className="font-display">Lead Availability</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">Control whether <span className="font-semibold text-slate-700">{avail?.name}</span> receives automatically assigned new leads (manual assignment always works).</p>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="text-sm font-medium text-slate-700">Unavailable for new leads</div><div className="text-xs text-slate-400">Auto/website routing will skip this person.</div></div>
              <Switch checked={!!avail?.unavailable_for_new_leads} onCheckedChange={(v) => setAvail({ ...avail, unavailable_for_new_leads: v })} data-testid="avail-toggle" />
            </div>
            <div>
              <Label className="text-xs">Backup Salesperson (optional)</Label>
              <Select value={avail?.backup_user_id || "none"} onValueChange={(v) => setAvail({ ...avail, backup_user_id: v === "none" ? null : v })}>
                <SelectTrigger className="mt-1" data-testid="avail-backup"><SelectValue placeholder="No backup" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No backup</SelectItem>
                  {rows.filter((r) => r.id !== avail?.id && (r.role === "sales_rep" || r.role === "sales_manager") && r.active).map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">When unavailable, their leads route to this active backup first.</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAvail(null)}>Cancel</Button><Button onClick={saveAvail} data-testid="avail-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}